import { formatSpan } from '../event/lifetime';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const ACTIVE_TOLERANCE_MS = 10 * 60 * 1000;

export const ALL_YEARS = 15;

export interface AssetQuickRange {
    id: string;
    label: string;
    spanMs: number | null;
}

// The desktop toolbar.
export const ASSET_QUICK_RANGES: readonly AssetQuickRange[] = [
    { id: '1w', label: '1W', spanMs: 7 * DAY_MS },
    { id: '1m', label: '1M', spanMs: 30 * DAY_MS },
    { id: '3m', label: '3M', spanMs: 90 * DAY_MS },
    { id: '1y', label: '1Y', spanMs: 365 * DAY_MS },
    { id: 'all', label: 'All', spanMs: null },
];

// The phone toolbar (below 900 px): the same five buttons as the event chart.
// 3M and 1Y exist only on the desktop toolbar.
export const ASSET_COMPACT_RANGES: readonly AssetQuickRange[] = [
    { id: '1h', label: '1H', spanMs: HOUR_MS },
    { id: '1d', label: '1D', spanMs: DAY_MS },
    { id: '1w', label: '1W', spanMs: 7 * DAY_MS },
    { id: '1m', label: '1M', spanMs: 30 * DAY_MS },
    { id: 'all', label: 'All', spanMs: null },
];

// Every range the page knows, once per id. rangeFor and activeQuickRange look
// here, so an address made with one toolbar opens on the other.
const ALL_RANGES: readonly AssetQuickRange[] = [
    ...ASSET_COMPACT_RANGES.filter(
        c => !ASSET_QUICK_RANGES.some(q => q.id === c.id)
    ),
    ...ASSET_QUICK_RANGES,
];

// How often an asset type has a new value. A range that holds no more than
// one such step shows an empty chart, so its button is not offered.
// FRED series (macro) are daily, one value per business day.
const CADENCE_MS: Readonly<Record<string, number>> = { macro: DAY_MS };

/**
 * The phone toolbar's ranges for an asset type.
 *
 * Known gap, left as it is for now: an IEX-backed asset (SPY, GOLD) has values
 * only while the US market is open, so its 1H and 1D buttons give an empty
 * chart outside those hours and at weekends, although they are offered here
 * (crypto trades round the clock and is not affected). Follow-up: take the
 * window from the last session, or offer the button only while the last value
 * is younger than its span, as CADENCE_MS does for macro series.
 */
export function compactRangesFor(
    assetType: string | null | undefined
): readonly AssetQuickRange[] {
    const cadence = assetType ? CADENCE_MS[assetType] : undefined;
    if (cadence === undefined) return ASSET_COMPACT_RANGES;
    return ASSET_COMPACT_RANGES.filter(
        q => q.spanMs === null || q.spanMs > cadence
    );
}

export const DEFAULT_QUICK_RANGE = '3m';

export function assetBounds(nowMs: number): [Date, Date] {
    return [new Date(nowMs - ALL_YEARS * 365 * DAY_MS), new Date(nowMs)];
}

export function rangeFor(
    id: string,
    bounds: readonly [Date, Date]
): [Date, Date] | null {
    const choice = ALL_RANGES.find(q => q.id === id);
    if (!choice) return null;
    if (choice.spanMs === null) return [bounds[0], bounds[1]];
    const end = bounds[1].getTime();
    const start = Math.max(bounds[0].getTime(), end - choice.spanMs);
    return [new Date(start), bounds[1]];
}

export function activeQuickRange(
    range: readonly [Date, Date],
    bounds: readonly [Date, Date]
): string | null {
    const endGap = Math.abs(range[1].getTime() - bounds[1].getTime());
    if (endGap > ACTIVE_TOLERANCE_MS) return null;
    const span = range[1].getTime() - range[0].getTime();
    for (const q of ALL_RANGES) {
        const wanted = q.spanMs ?? bounds[1].getTime() - bounds[0].getTime();
        if (Math.abs(span - wanted) <= ACTIVE_TOLERANCE_MS) return q.id;
    }
    return null;
}

/** The label of the shown range for the header: "1D", or its length ("15d"). */
export function assetRangeLabel(
    range: readonly [Date, Date],
    bounds: readonly [Date, Date]
): string {
    const id = activeQuickRange(range, bounds);
    const label = ALL_RANGES.find(q => q.id === id)?.label;
    return label ?? formatSpan(range[1].getTime() - range[0].getTime());
}
